import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, query, where, orderBy, serverTimestamp
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import LoadingSpinner from './components/LoadingSpinner';
import Modal from './components/Modal';
import FlowchartEditor from './components/FlowchartEditor';
import WhiteboardEditor from './components/WhiteboardEditor';
import { effectiveFolderId, buildFolderMoveUpdate } from './utils/folderAssignment';
import { buildPermalink, copyToClipboard, pinEntityToDesktop } from './utils/shortcutHelpers';
import './DiagrammingApp.css';

function DiagrammingApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [diagrams, setDiagrams] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null); // null = "My Diagrams"
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [editingDiagram, setEditingDiagram] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'flowchart' | 'whiteboard' | null
  
  // Folder management state
  const [isFolderFormOpen, setIsFolderFormOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [isFolderShareModalOpen, setIsFolderShareModalOpen] = useState(false);
  const [sharingFolder, setSharingFolder] = useState(null);
  
  // Diagram sharing state
  const [isDiagramShareModalOpen, setIsDiagramShareModalOpen] = useState(false);
  const [sharingDiagram, setSharingDiagram] = useState(null);
  
  const [newDiagramData, setNewDiagramData] = useState({
    title: '',
    applicationId: null,
    folderId: null
  });

  const [folderShareData, setFolderShareData] = useState({
    userEmail: '',
    permission: 'view'
  });

  const [diagramShareData, setDiagramShareData] = useState({
    userEmail: '',
    permission: 'view'
  });

  const role = userProfile?.role || null;

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'Diagramming - Wixon & Cross Desk Suite';
  }, []);

  // Diagrams listener
  useEffect(() => {
    if (!user) return;
    
    const q = query(collection(db, 'diagrams'));
    
    const unsub = onSnapshot(q, 
      (snap) => {
        const allDiagrams = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Show user's own diagrams AND diagrams shared with them
        const userDiagrams = allDiagrams.filter(diagram => 
          diagram.createdBy === user.uid || 
          diagram.sharedWith?.some(s => s.userId === user.uid)
        );
        
        // Sort by updatedAt in memory
        userDiagrams.sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || 0;
          const bTime = b.updatedAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
        setDiagrams(userDiagrams);
        console.log('Loaded diagrams:', userDiagrams.length);
      },
      (error) => {
        console.error('Error loading diagrams:', error);
      }
    );
    
    return () => unsub();
  }, [user]);

  // Folders listener
  useEffect(() => {
    if (!user) return;
    
    const foldersQuery = query(collection(db, 'diagramFolders'));
    
    const unsub = onSnapshot(foldersQuery, snap => {
      const allFolders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Show user's own folders AND folders shared with them
      const userFolders = allFolders.filter(folder => 
        folder.createdBy === user.uid || 
        folder.sharedWith?.some(s => s.userId === user.uid)
      );
      
      setFolders(userFolders);
    });
    
    return () => unsub();
  }, [user]);

  // Users listener
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Applications listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleCreateDiagram = async (mode) => {
    if (!newDiagramData.title.trim()) {
      alert('Please enter a diagram title');
      return;
    }

    try {
      const targetFolderId = newDiagramData.folderId || currentFolder || null;
      
      // Check if folder is shared and auto-share diagram
      let initialSharedWith = [];
      if (targetFolderId) {
        const targetFolder = folders.find(f => f.id === targetFolderId);
        if (targetFolder && targetFolder.sharedWith && targetFolder.sharedWith.length > 0) {
          initialSharedWith = targetFolder.sharedWith;
        }
      }

      const diagramDoc = await addDoc(collection(db, 'diagrams'), {
        title: newDiagramData.title,
        mode: mode, // 'flowchart' or 'whiteboard'
        content: mode === 'flowchart' ? { nodes: [], edges: [] } : { elements: [] },
        applicationId: newDiagramData.applicationId || null,
        folderId: targetFolderId,
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sharedWith: initialSharedWith
      });

      // Open the diagram in edit mode
      const newDiagram = {
        id: diagramDoc.id,
        title: newDiagramData.title,
        mode: mode,
        content: mode === 'flowchart' ? { nodes: [], edges: [] } : { elements: [] },
        applicationId: newDiagramData.applicationId || null,
        folderId: targetFolderId
      };

      setEditingDiagram(newDiagram);
      setViewMode(mode);
      setShowModeSelector(false);
      setNewDiagramData({ title: '', applicationId: null, folderId: null });
    } catch (err) {
      console.error('Error creating diagram:', err);
      alert('Error creating diagram');
    }
  };

  const handleOpenDiagram = (diagram) => {
    setEditingDiagram(diagram);
    setViewMode(diagram.mode);
  };

  const handleCloseDiagram = () => {
    setEditingDiagram(null);
    setViewMode(null);
  };

  const handleSaveDiagram = async (content) => {
    if (!editingDiagram) return;

    try {
      console.log('Saving diagram:', editingDiagram.id, content);
      await updateDoc(doc(db, 'diagrams', editingDiagram.id), {
        content: content,
        updatedAt: serverTimestamp()
      });
      console.log('Diagram saved successfully!');
    } catch (err) {
      console.error('Error saving diagram:', err);
      alert('Error saving diagram');
    }
  };

  const handleDeleteDiagram = async (diagramId) => {
    if (!window.confirm('Delete this diagram?')) return;

    try {
      await deleteDoc(doc(db, 'diagrams', diagramId));
    } catch (err) {
      console.error('Error deleting diagram:', err);
      alert('Error deleting diagram');
    }
  };

  // Build 27 (item 1): move a diagram into one of the user's folders.
  // Uses the shared buildFolderMoveUpdate so owners change folderId and
  // recipients of a shared diagram only set their own folderAssignments
  // slot — owner's organization untouched.
  const handleMoveDiagramToFolder = async (diagram, targetFolderId) => {
    if (!diagram?.id || !user?.uid) return;
    try {
      const update = buildFolderMoveUpdate(diagram, user.uid, targetFolderId);
      await updateDoc(doc(db, 'diagrams', diagram.id), update);
    } catch (err) {
      console.error('Error moving diagram to folder:', err);
      alert(`Could not move diagram: ${err?.message || 'unknown error'}`);
    }
  };

  // Folder Management Functions
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    try {
      await addDoc(collection(db, 'diagramFolders'), {
        name: folderName,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        sharedWith: []
      });
      setFolderName('');
      setIsFolderFormOpen(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      alert('Error creating folder');
    }
  };

  const handleEditFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim() || !editingFolder) return;

    try {
      await updateDoc(doc(db, 'diagramFolders', editingFolder.id), {
        name: folderName
      });
      setFolderName('');
      setEditingFolder(null);
      setIsFolderFormOpen(false);
    } catch (err) {
      console.error('Error updating folder:', err);
      alert('Error updating folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?\n\nDiagrams in this folder will be moved to "My Diagrams".`)) return;

    try {
      // Move all diagrams in this folder to "My Diagrams" (folderId = null)
      const folderDiagrams = diagrams.filter(d => d.folderId === folder.id);
      await Promise.all(
        folderDiagrams.map(diagram =>
          updateDoc(doc(db, 'diagrams', diagram.id), { folderId: null })
        )
      );

      // Delete the folder
      await deleteDoc(doc(db, 'diagramFolders', folder.id));

      // If currently viewing this folder, switch to "My Diagrams"
      if (currentFolder === folder.id) {
        setCurrentFolder(null);
      }
    } catch (err) {
      console.error('Error deleting folder:', err);
      alert('Error deleting folder');
    }
  };

  const openEditFolderModal = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setIsFolderFormOpen(true);
  };

  const closeFolderModal = () => {
    setIsFolderFormOpen(false);
    setEditingFolder(null);
    setFolderName('');
  };

  // Folder Sharing Functions
  const openFolderShareModal = (folder) => {
    setSharingFolder(folder);
    setFolderShareData({ userEmail: '', permission: 'view' });
    setIsFolderShareModalOpen(true);
  };

  const handleShareFolder = async (e) => {
    e.preventDefault();
    
    const targetUser = users.find(u => u.email === folderShareData.userEmail);
    if (!targetUser) {
      alert('User not found');
      return;
    }
    
    if (targetUser.id === user.uid) {
      alert('You cannot share a folder with yourself');
      return;
    }
    
    const alreadyShared = sharingFolder.sharedWith?.some(s => s.userId === targetUser.id);
    if (alreadyShared) {
      alert('Folder is already shared with this user');
      return;
    }
    
    try {
      const updatedSharedWith = [
        ...(sharingFolder.sharedWith || []),
        {
          userId: targetUser.id,
          userEmail: targetUser.email,
          permission: folderShareData.permission,
          sharedAt: new Date().toISOString()
        }
      ];
      
      // Update folder with new share
      await updateDoc(doc(db, 'diagramFolders', sharingFolder.id), {
        sharedWith: updatedSharedWith
      });
      
      // Share all diagrams in this folder with the user
      const folderDiagrams = diagrams.filter(d => d.folderId === sharingFolder.id);
      await Promise.all(
        folderDiagrams.map(diagram => {
          const diagramAlreadyShared = diagram.sharedWith?.some(s => s.userId === targetUser.id);
          if (!diagramAlreadyShared) {
            return updateDoc(doc(db, 'diagrams', diagram.id), {
              sharedWith: [
                ...(diagram.sharedWith || []),
                {
                  userId: targetUser.id,
                  userEmail: targetUser.email,
                  permission: folderShareData.permission,
                  sharedAt: new Date().toISOString()
                }
              ]
            });
          }
          return Promise.resolve();
        })
      );
      
      setIsFolderShareModalOpen(false);
      setSharingFolder(null);
      alert(`Folder shared with ${targetUser.email}`);
    } catch (err) {
      console.error('Error sharing folder:', err);
      alert('Error sharing folder');
    }
  };

  const handleRemoveFolderShare = async (folder, shareEntry) => {
    if (!window.confirm(`Remove ${shareEntry.userEmail} from this folder?`)) return;
    
    try {
      const updatedSharedWith = folder.sharedWith.filter(s => s.userId !== shareEntry.userId);
      
      await updateDoc(doc(db, 'diagramFolders', folder.id), {
        sharedWith: updatedSharedWith
      });
      
      alert(`Removed ${shareEntry.userEmail} from folder`);
    } catch (err) {
      console.error('Error removing folder share:', err);
      alert('Error removing share');
    }
  };

  // Diagram Sharing Functions
  const openDiagramShareModal = (diagram) => {
    setSharingDiagram(diagram);
    setDiagramShareData({ userEmail: '', permission: 'view' });
    setIsDiagramShareModalOpen(true);
  };

  const handleShareDiagram = async (e) => {
    e.preventDefault();
    
    const targetUser = users.find(u => u.email === diagramShareData.userEmail);
    if (!targetUser) {
      alert('User not found');
      return;
    }
    
    if (targetUser.id === user.uid) {
      alert('You cannot share a diagram with yourself');
      return;
    }
    
    const alreadyShared = sharingDiagram.sharedWith?.some(s => s.userId === targetUser.id);
    if (alreadyShared) {
      alert('Diagram is already shared with this user');
      return;
    }
    
    try {
      const updatedSharedWith = [
        ...(sharingDiagram.sharedWith || []),
        {
          userId: targetUser.id,
          userEmail: targetUser.email,
          permission: diagramShareData.permission,
          sharedAt: new Date().toISOString()
        }
      ];
      
      await updateDoc(doc(db, 'diagrams', sharingDiagram.id), {
        sharedWith: updatedSharedWith
      });
      
      setIsDiagramShareModalOpen(false);
      setSharingDiagram(null);
      alert(`Diagram shared with ${targetUser.email}`);
    } catch (err) {
      console.error('Error sharing diagram:', err);
      alert('Error sharing diagram');
    }
  };

  const handleRemoveDiagramShare = async (diagram, shareEntry) => {
    if (!window.confirm(`Remove ${shareEntry.userEmail} from this diagram?`)) return;
    
    try {
      const updatedSharedWith = diagram.sharedWith.filter(s => s.userId !== shareEntry.userId);
      
      await updateDoc(doc(db, 'diagrams', diagram.id), {
        sharedWith: updatedSharedWith
      });
      
      alert(`Removed ${shareEntry.userEmail} from diagram`);
    } catch (err) {
      console.error('Error removing diagram share:', err);
      alert('Error removing share');
    }
  };

  // Filter diagrams by current folder
  const getFilteredDiagrams = () => {
    if (currentFolder === null) {
      // "My Diagrams" - show only diagrams the user hasn't filed
      return diagrams.filter(d => !effectiveFolderId(d, user?.uid));
    } else {
      return diagrams.filter(d => effectiveFolderId(d, user?.uid) === currentFolder);
    }
  };

  const filteredDiagrams = getFilteredDiagrams();

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Loading..." />;

  // If viewing/editing a diagram, show the appropriate editor
  if (editingDiagram && viewMode) {
    if (viewMode === 'flowchart') {
      return (
        <FlowchartEditor
          diagram={editingDiagram}
          onClose={handleCloseDiagram}
          onSave={handleSaveDiagram}
        />
      );
    } else if (viewMode === 'whiteboard') {
      return (
        <WhiteboardEditor
          diagram={editingDiagram}
          onClose={handleCloseDiagram}
          onSave={handleSaveDiagram}
        />
      );
    }
  }

  return (
    <div className="diagramming-app">
      <header className="header">
        <div className="header-content">
          <div>
            <div className="breadcrumb-nav-app">
              <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
                🖥️ Desktop
              </button>
              <span className="breadcrumb-separator">›</span>
              <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
                📦 Dashboard
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-item breadcrumb-current">🎨 Diagramming</span>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowModeSelector(true)}>
              + New Diagram
            </button>
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge">{role}</span></span>
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="app-container">
        {/* Sidebar with Folders */}
        <aside className="sidebar">
          <div className="sidebar-section">
            <div className="sidebar-header">
              <h3>Folders</h3>
              <button 
                className="btn-icon-small" 
                onClick={() => setIsFolderFormOpen(true)}
                title="New Folder"
              >
                ➕
              </button>
            </div>
            
            <div 
              className={`folder-item ${currentFolder === null ? 'active' : ''}`}
              onClick={() => setCurrentFolder(null)}
            >
              <span className="folder-icon">📁</span>
              <span className="folder-name">My Diagrams</span>
              <span className="folder-count">({diagrams.filter(d => !d.folderId).length})</span>
            </div>

            {folders.map(folder => {
              const folderDiagramCount = diagrams.filter(d => effectiveFolderId(d, user?.uid) === folder.id).length;
              const isShared = folder.sharedWith && folder.sharedWith.length > 0;
              const isOwner = folder.createdBy === user.uid;
              
              return (
                <div 
                  key={folder.id}
                  className={`folder-item ${currentFolder === folder.id ? 'active' : ''}`}
                  onClick={() => setCurrentFolder(folder.id)}
                >
                  <span className="folder-icon">📂</span>
                  <span className="folder-name">
                    {folder.name}
                    {isShared && <span className="shared-indicator" title="Shared folder">👥</span>}
                  </span>
                  <span className="folder-count">({folderDiagramCount})</span>
                  <div className="folder-actions" onClick={e => e.stopPropagation()}>
                    {isOwner && (
                      <>
                        <button 
                          className="btn-icon-small" 
                          onClick={() => openFolderShareModal(folder)}
                          title="Share Folder"
                        >
                          👥
                        </button>
                        <button 
                          className="btn-icon-small" 
                          onClick={() => openEditFolderModal(folder)}
                          title="Edit Folder"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn-icon-small" 
                          onClick={() => handleDeleteFolder(folder)}
                          title="Delete Folder"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {filteredDiagrams.length === 0 ? (
            <div className="empty-state">
              <h2>No Diagrams {currentFolder ? 'in This Folder' : 'Yet'}</h2>
              <p>Create your first diagram to get started!</p>
              <button className="btn btn-primary" onClick={() => setShowModeSelector(true)}>
                + Create Diagram
              </button>
            </div>
          ) : (
            <div className="diagrams-grid">
              {filteredDiagrams.map(diagram => {
                const isShared = diagram.sharedWith && diagram.sharedWith.length > 0;
                const isOwner = diagram.createdBy === user.uid;
                
                return (
                  <div key={diagram.id} className="diagram-card">
                    <div className="diagram-card-header">
                      <h3>
                        {diagram.title}
                        {isShared && <span className="shared-indicator" title="Shared diagram">👥</span>}
                      </h3>
                      <span className={`mode-badge mode-${diagram.mode}`}>
                        {diagram.mode === 'flowchart' ? '📊 Flowchart' : '✏️ Whiteboard'}
                      </span>
                    </div>
                    <div className="diagram-card-body">
                      <p className="diagram-date">
                        Updated: {diagram.updatedAt?.toDate().toLocaleDateString()}
                      </p>
                      {diagram.applicationId && (
                        <p className="diagram-app">
                          Linked to: {applications.find(a => a.id === diagram.applicationId)?.name || 'Unknown'}
                        </p>
                      )}
                    </div>
                    <div className="diagram-card-actions">
                      <button 
                        className="btn btn-primary btn-small"
                        onClick={() => handleOpenDiagram(diagram)}
                      >
                        Open
                      </button>
                      {isOwner && (
                        <button 
                          className="btn btn-secondary btn-small"
                          onClick={() => openDiagramShareModal(diagram)}
                        >
                          👥 Share
                        </button>
                      )}
                      {/* Build 28 (item 1): Copy Link + Pin to Desktop so
                          a diagram can be opened as a desktop window. */}
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={async () => {
                          const url = buildPermalink('/diagrams', diagram.id);
                          const ok = await copyToClipboard(url);
                          alert(ok ? 'Link copied to clipboard!' : 'Could not copy link');
                        }}
                        title="Copy a shareable link to this diagram"
                      >
                        🔗 Copy Link
                      </button>
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={async () => {
                          try {
                            await pinEntityToDesktop({
                              userId: user.uid,
                              type: 'diagram',
                              entityId: diagram.id,
                              title: diagram.title,
                              icon: diagram.mode === 'flowchart' ? '📊' : '✏️'
                            });
                            alert('Pinned to desktop');
                          } catch (err) {
                            alert(`Could not pin: ${err?.message || 'unknown error'}`);
                          }
                        }}
                        title="Pin this diagram to your desktop"
                      >
                        📎 Pin
                      </button>
                      {/* Build 27 (item 1): move diagram into one of my
                          folders. Recipients of a shared diagram file it
                          via their own folderAssignments slot. */}
                      <select
                        className="btn btn-secondary btn-small"
                        value=""
                        onChange={(e) => {
                          if (e.target.value !== '') {
                            const dest = e.target.value === '__root__' ? null : e.target.value;
                            handleMoveDiagramToFolder(diagram, dest);
                            e.target.value = '';
                          }
                        }}
                        title="Move this diagram into one of your folders"
                      >
                        <option value="">📂 Move to…</option>
                        <option value="__root__">My Diagrams (no folder)</option>
                        {folders.filter(f => f.createdBy === user?.uid).map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                      {isOwner && (
                        <button 
                          className="btn btn-secondary btn-small"
                          onClick={() => handleDeleteDiagram(diagram.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Mode Selector Modal */}
      <Modal
        isOpen={showModeSelector}
        onClose={() => {
          setShowModeSelector(false);
          setNewDiagramData({ title: '', applicationId: null, folderId: null });
        }}
        title="Create New Diagram"
      >
        <div className="mode-selector">
          <div className="form-group">
            <label>Diagram Title *</label>
            <input
              type="text"
              value={newDiagramData.title}
              onChange={e => setNewDiagramData({ ...newDiagramData, title: e.target.value })}
              placeholder="Enter diagram title"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Folder (Optional)</label>
            <select
              value={newDiagramData.folderId || ''}
              onChange={e => setNewDiagramData({ ...newDiagramData, folderId: e.target.value || null })}
            >
              <option value="">My Diagrams</option>
              {folders.map(folder => (
                <option key={folder.id} value={folder.id}>{folder.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Link to Application (Optional)</label>
            <select
              value={newDiagramData.applicationId || ''}
              onChange={e => setNewDiagramData({ ...newDiagramData, applicationId: e.target.value || null })}
            >
              <option value="">No Application</option>
              {applications.map(app => (
                <option key={app.id} value={app.id}>{app.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Choose Diagram Type</label>
            <div className="mode-options">
              <div 
                className="mode-option"
                onClick={() => handleCreateDiagram('flowchart')}
              >
                <div className="mode-icon">📊</div>
                <h4>Flowchart</h4>
                <p>Structured diagrams with shapes and connectors</p>
                <small>Best for: Process flows, org charts, system diagrams</small>
              </div>

              <div 
                className="mode-option"
                onClick={() => handleCreateDiagram('whiteboard')}
              >
                <div className="mode-icon">✏️</div>
                <h4>Whiteboard</h4>
                <p>Freeform sketching and drawing</p>
                <small>Best for: Brainstorming, mockups, quick sketches</small>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Folder Form Modal */}
      <Modal
        isOpen={isFolderFormOpen}
        onClose={closeFolderModal}
        title={editingFolder ? 'Edit Folder' : 'New Folder'}
      >
        <form onSubmit={editingFolder ? handleEditFolder : handleCreateFolder}>
          <div className="form-group">
            <label>Folder Name *</label>
            <input
              type="text"
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              required
              placeholder="Enter folder name"
              autoFocus
            />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn btn-primary">
              {editingFolder ? 'Save' : 'Create'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeFolderModal}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Folder Share Modal */}
      <Modal
        isOpen={isFolderShareModalOpen}
        onClose={() => {
          setIsFolderShareModalOpen(false);
          setSharingFolder(null);
        }}
        title={`Share Folder: ${sharingFolder?.name || ''}`}
      >
        <form onSubmit={handleShareFolder}>
          <div className="form-group">
            <label>Share with User *</label>
            <select
              value={folderShareData.userEmail}
              onChange={e => setFolderShareData({ ...folderShareData, userEmail: e.target.value })}
              required
            >
              <option value="">Select user...</option>
              {users.filter(u => u.id !== user.uid).map(u => (
                <option key={u.id} value={u.email}>{u.email}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Permission *</label>
            <select
              value={folderShareData.permission}
              onChange={e => setFolderShareData({ ...folderShareData, permission: e.target.value })}
            >
              <option value="view">View Only</option>
              <option value="edit">Can Edit</option>
            </select>
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn btn-primary">
              Share Folder
            </button>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => {
                setIsFolderShareModalOpen(false);
                setSharingFolder(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {sharingFolder && sharingFolder.sharedWith && sharingFolder.sharedWith.length > 0 && (
          <div className="shared-with-section">
            <h4>Currently Shared With:</h4>
            <ul className="shared-with-list">
              {sharingFolder.sharedWith.map((shareEntry, idx) => (
                <li key={idx} className="shared-entry">
                  <div>
                    <strong>{shareEntry.userEmail}</strong>
                    <span className="permission-badge">{shareEntry.permission}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    onClick={() => handleRemoveFolderShare(sharingFolder, shareEntry)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <p className="info-text">
              ℹ️ All diagrams in this folder are automatically shared with these users
            </p>
          </div>
        )}
      </Modal>

      {/* Diagram Share Modal */}
      <Modal
        isOpen={isDiagramShareModalOpen}
        onClose={() => {
          setIsDiagramShareModalOpen(false);
          setSharingDiagram(null);
        }}
        title={`Share Diagram: ${sharingDiagram?.title || ''}`}
      >
        <form onSubmit={handleShareDiagram}>
          <div className="form-group">
            <label>Share with User *</label>
            <select
              value={diagramShareData.userEmail}
              onChange={e => setDiagramShareData({ ...diagramShareData, userEmail: e.target.value })}
              required
            >
              <option value="">Select user...</option>
              {users.filter(u => u.id !== user.uid).map(u => (
                <option key={u.id} value={u.email}>{u.email}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Permission *</label>
            <select
              value={diagramShareData.permission}
              onChange={e => setDiagramShareData({ ...diagramShareData, permission: e.target.value })}
            >
              <option value="view">View Only</option>
              <option value="edit">Can Edit</option>
            </select>
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn btn-primary">
              Share Diagram
            </button>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => {
                setIsDiagramShareModalOpen(false);
                setSharingDiagram(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {sharingDiagram && sharingDiagram.sharedWith && sharingDiagram.sharedWith.length > 0 && (
          <div className="shared-with-section">
            <h4>Currently Shared With:</h4>
            <ul className="shared-with-list">
              {sharingDiagram.sharedWith.map((shareEntry, idx) => (
                <li key={idx} className="shared-entry">
                  <div>
                    <strong>{shareEntry.userEmail}</strong>
                    <span className="permission-badge">{shareEntry.permission}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    onClick={() => handleRemoveDiagramShare(sharingDiagram, shareEntry)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default DiagrammingApp;
